import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import { getMyStudentProjects } from '../../services/studentService';
import { getProjectMilestones } from '../../services/projectService';
import { formatDate } from '../../utils/helpers';
import styles from './StudentProjects.module.css';

const StudentProjects = () => {
  const [projects, setProjects] = useState([]);
  const [projectMilestones, setProjectMilestones] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await getMyStudentProjects();
      setProjects(data);
      
      // Fetch milestones for each project to show progress
      const milestonesData = {};
      for (const project of data) {
        try {
          const milestones = await getProjectMilestones(project.id);
          milestonesData[project.id] = milestones;
        } catch (error) {
          console.error(`Error fetching milestones for project ${project.id}:`, error);
          milestonesData[project.id] = [];
        }
      }
      setProjectMilestones(milestonesData);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMilestoneProgress = (projectId) => {
    const milestones = projectMilestones[projectId] || [];
    const total = milestones.length;
    const completed = milestones.filter(m => m.status === 'completed' || m.status === 'approved').length;
    return { completed, total };
  };

  const handleViewProject = (project) => {
    navigate(`/student/projects/${project.id}`);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading projects..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>My Projects</h1>
      </div>

      {projects.length === 0 ? (
        <Card>
          <div className={styles.empty}>
            <p>No projects assigned yet</p>
          </div>
        </Card>
      ) : (
        <div className={styles.projectsGrid}>
          {projects.map(project => {
            const { completed, total } = getMilestoneProgress(project.id);
            const progressPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;
            
            return (
              <Card key={project.id} hover className={styles.projectCard}>
                <div className={styles.projectHeader}>
                  <h3 className={styles.projectTitle}>{project.project_name}</h3>
                  <StatusBadge status={project.status} />
                </div>
                
                <div className={styles.projectDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Track:</span>
                    <span className={styles.value}>
                      {project.project_track?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                  
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Start Date:</span>
                    <span className={styles.value}>{formatDate(project.start_date)}</span>
                  </div>
                  
                  {project.end_date && (
                    <div className={styles.detailRow}>
                      <span className={styles.label}>End Date:</span>
                      <span className={styles.value}>{formatDate(project.end_date)}</span>
                    </div>
                  )}
                  
                  <div className={styles.progressSection}>
                    <div className={styles.progressLabel}>
                      <span className={styles.label}>Progress:</span>
                      <span className={styles.progressText}>
                        {completed}/{total} Milestones Completed
                      </span>
                    </div>
                    <div className={styles.progressBar}>
                      <div 
                        className={styles.progressFill} 
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                    <span className={styles.progressPercentage}>{progressPercentage}%</span>
                  </div>
                  
                  {project.project_scope && (
                    <div className={styles.scopeStatus}>
                      <span className={styles.scopeLabel}>Scope:</span>
                      <span className={project.scope_approved ? styles.approved : styles.pending}>
                        {project.scope_approved ? 'Approved' : 'Pending Approval'}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className={styles.projectActions}>
                  <Button size="small" 
                    fullWidth
                    onClick={() => handleViewProject(project)} loading={loading}
                  >
                    View Details
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentProjects;

